import cx from 'classnames';
import {
  FileType,
  VideoUrlType,
} from 'components/admin/content-library/default/CourseUpsertSidePanel/types';
import {
  ICustomFileModule,
  ICustomPdfModule,
  ICustomVideoModule,
  ICustomVideoUrlModule,
} from 'components/admin/content-library/default/types';
import { Anchor, Button, Form } from 'components/admin/ui';
import { useAuthContext } from 'components/auth';
import { Tooltip } from 'components/ui';
import pluralHelper from 'lib/helpers/pluralHelper';
import { IModule, ModuleType } from 'lib/types/course';
import { MouseEvent, ReactNode } from 'react';
import { ModuleAttributes } from '../ModuleAttributes/ModuleAttributes';
import css from './ModuleSummaryCard.module.scss';

interface IModuleSummaryCardProps {
  children?: ReactNode;
  className?: string;
  // ICustomFileModule | ICustomVideoUrlModule will be deprecated once we cut over to the new content library
  module:
    | IModule
    | ICustomFileModule
    | ICustomVideoModule
    | ICustomVideoUrlModule
    | ICustomPdfModule;
  /**
   * Properties in between comment blocks are to be removed when this component only accepts `IModule`s
   */
  belongsToModuleNames?: string[];
  moduleType?: FileType | VideoUrlType;
  isContinuousLearning?: boolean;
  isCustomCourse: boolean;
  includedNudgeGroupNames?: string[];
  /**
   * End comment block
   */
  isSelected?: boolean;
  isDeleted?: boolean;
  onEditClick?: () => void;
  onSelect?: () => void;
  showCheckbox?: boolean;
}

export const ModuleSummaryCard = ({
  belongsToModuleNames,
  children,
  className,
  includedNudgeGroupNames,
  isContinuousLearning,
  isDeleted = false,
  isSelected,
  module,
  moduleType,
  isCustomCourse,
  onEditClick,
  onSelect,
  showCheckbox = false,
}: IModuleSummaryCardProps) => {
  const { session } = useAuthContext();

  const isSelectable = !!onSelect;
  const Component = isSelectable ? 'button' : 'div';

  const isCompanyConfigured = !!(module as IModule).companyId;

  const getModuleName = () => {
    const moduleName: string =
      isCompanyConfigured && !isCustomCourse
        ? `${module.name} (${session.company.name})`
        : module.name;

    return isDeleted ? `[Deprecated] ${moduleName}` : moduleName;
  };

  const renderPreviewUrl = () => {
    if (isCustomCourse) {
      const previewUrl = /engine\.scorm\.com/gi.test(module.previewUrl)
        ? `/training-preview?iframeUrl=${encodeURIComponent(module.previewUrl)}`
        : module.previewUrl;

      return (
        <>
          <span className={css.separator} />
          <Tooltip
            title={
              module.previewUrl
                ? undefined
                : 'Preview is unavailable for this file type'
            }
            placement="top"
          >
            <Anchor
              className={css.previewLink}
              isDisabled={!module.previewUrl}
              href={previewUrl}
              onClick={(ev: MouseEvent<HTMLAnchorElement>) =>
                ev.stopPropagation()
              }
              target="_blank"
            >
              Preview Content
            </Anchor>
          </Tooltip>
        </>
      );
    } else if (module.previewUrl) {
      return (
        <>
          <span className={css.separator} />
          <Anchor
            className={css.previewLink}
            href={module.previewUrl}
            onClick={(ev: MouseEvent<HTMLAnchorElement>) =>
              ev.stopPropagation()
            }
            target="_blank"
          >
            Preview Content
          </Anchor>
        </>
      );
    } else {
      return null;
    }
  };

  return (
    <Component
      className={cx(css.container, className, {
        [css.selected]: isSelected,
      })}
      onClick={
        isSelectable
          ? (ev: MouseEvent) => {
              onSelect();
              ev.preventDefault();
            }
          : undefined
      }
    >
      <div className={css.body}>
        <div className={css.title}>
          <div>
            <p className={css.moduleType}>{moduleType}</p>
            {showCheckbox ? (
              <Form.Checkbox
                checked={isSelected}
                className={css.checkbox}
                onChange={onSelect}
                label={getModuleName()}
                size={14}
              />
            ) : (
              getModuleName()
            )}
          </div>
          {onEditClick && (
            <Button
              className={css.editButton}
              onClick={onEditClick}
              size="sm"
              variant="outline-light"
            >
              Edit
            </Button>
          )}
        </div>

        <div className="d-flex align-items-center">
          <p className={css.length}>
            {module.lengthInMinutes} Minute
            {pluralHelper(module.lengthInMinutes)}
          </p>
          {renderPreviewUrl()}
        </div>

        {module.description && (
          <p className={css.description}>{module.description}</p>
        )}
      </div>

      <ModuleAttributes
        belongsToModuleNames={belongsToModuleNames}
        includedNudgeGroupNames={includedNudgeGroupNames}
        isContinuousLearning={isContinuousLearning}
        // TODO: Remove the `isCustomCourse` condition when IModule is the only
        // type accepted
        isCustomContent={
          (module as IModule).type === ModuleType.IMPORTED_CUSTOM_MODULE ||
          isCustomCourse
        }
      />

      {children}
    </Component>
  );
};
